/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  FormGroup,
  Card,
  CardBody,
  Button,
  Label,
  CustomInput,
  Alert,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
} from 'reactstrap';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import Input from '~/components/Inputs/Input';
import InputSelectAsync from '~/components/Inputs/InputSelectAsync';
import Loading from '~/components/Loading';
import PageHeader from '~/components/Common/PageHeader';
import IconButton from '~/components/IconButton';
import { defineAlignment } from '~/util/newLayoutHelpers';
import { LG } from '~/util/breakpointWidths';
import { copyStringToClipboard } from '~/util/copyToClipboard';
import api from '~/services/api';
import history from '~/services/history';

import SecretRevealModal from './SecretRevealModal';
import RevokeModal from './RevokeModal';

const AVAILABLE_SCOPES = [
  { value: 'carts:read', label: 'Carrinhos — leitura' },
  {
    value: 'carts:write',
    label: 'Carrinhos — escrita (criar/atualizar/cancelar)',
  },
  { value: 'products:read', label: 'Produtos — leitura' },
  { value: 'stores:read', label: 'Lojas — leitura' },
  { value: 'clients:read', label: 'Clientes — leitura' },
  { value: 'clients:write', label: 'Clientes — escrita' },
  { value: 'webhooks:read', label: 'Webhooks — leitura' },
  {
    value: 'webhooks:write',
    label: 'Webhooks — escrita (criar/editar/deletar)',
  },
];

const EditPartnerApplication = () => {
  const { application_id } = useParams();
  const formRef = useRef(null);
  const windowSize = useSelector(({ layout }) => layout.windowSize);

  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);

  const [selectedScopes, setSelectedScopes] = useState([]);
  const [scopesError, setScopesError] = useState(null);

  const [allStores, setAllStores] = useState(true);
  const [showStorePicker, setShowStorePicker] = useState(false);
  const [storesSearched, setStoresSearched] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);

  const [allowedIpsText, setAllowedIpsText] = useState('');

  // Modais
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealData, setRevealData] = useState(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [reactivateConfirmOpen, setReactivateConfirmOpen] = useState(false);

  const baseUrl = useMemo(
    () => `administrator/partner-applications/${application_id}`,
    [application_id]
  );

  const status = useMemo(() => {
    if (!application) return null;
    if (application.revoked_at) {
      return { label: 'Revogada', bg: '#ef4444' };
    }
    if (!application.is_enabled) {
      return { label: 'Desativada', bg: '#9ca3af' };
    }
    return { label: 'Ativa', bg: '#10b981' };
  }, [application]);

  const fillFormFromApplication = data => {
    setApplication(data);
    setSelectedScopes(data.scopes || []);
    setAllStores(Boolean(data.manages_all_stores));
    setSelectedStores(
      (data.stores || []).map(s => ({ value: s.id, label: s.fantasy_name }))
    );
    setAllowedIpsText((data.allowed_ips || []).join('\n'));

    setTimeout(() => {
      formRef.current?.setData?.({
        name: data.name || '',
        description: data.description || '',
        rate_limit_per_minute: data.rate_limit_per_minute || 60,
      });
    }, 0);
  };

  useEffect(() => {
    document.title = 'Editar Aplicação | Viddy';
    const loadApp = async () => {
      try {
        const { data } = await api.get(baseUrl);
        fillFormFromApplication(data?.data || data);
      } catch (err) {
        if (err?.response?.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error('Aplicação não encontrada.');
        }
        history.push('/aplicacoes-parceiras');
      } finally {
        setLoading(false);
      }
    };
    loadApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application_id]);

  const toggleScope = scope => {
    setScopesError(null);
    setSelectedScopes(current => {
      if (current.includes(scope)) {
        return current.filter(s => s !== scope);
      }
      return [...current, scope];
    });
  };

  const loadStoreOptions = async (inputValue, cb) => {
    if (inputValue.length < 2) return;
    try {
      const { data } = await api.get('/stores', {
        params: { query: inputValue },
      });
      if (!data?.stores?.length) return;
      const filtered = data.stores.filter(
        s => !selectedStores.some(store => store.value === s.id)
      );
      const options = filtered.map(s => ({
        value: s.id,
        label: s.fantasy_name,
      }));
      setStoresSearched(prev => [...prev, ...data.stores]);
      cb(options);
    } catch (err) {
      // silencia
    }
  };

  const addStore = option => {
    if (!option) return;
    const store = storesSearched.find(p => p.id === option.value);
    if (!store) return;
    setSelectedStores([
      ...selectedStores,
      { value: store.id, label: store.fantasy_name },
    ]);
    setShowStorePicker(false);
  };

  const removeStore = option => {
    setSelectedStores(selectedStores.filter(s => s.value !== option.value));
  };

  const parseAllowedIps = () => {
    return allowedIpsText
      .split(/[\n,]/)
      .map(s => s.trim())
      .filter(Boolean);
  };

  // -----------------------------
  // Salvar alterações
  // -----------------------------
  const onSubmit = async data => {
    try {
      formRef.current.setErrors({});

      const schema = Yup.object().shape({
        name: Yup.string()
          .min(3, 'Mínimo 3 caracteres')
          .max(120, 'Máximo 120 caracteres')
          .required('O nome é obrigatório'),
        description: Yup.string().max(1000, 'Máximo 1000 caracteres'),
        rate_limit_per_minute: Yup.number()
          .typeError('Deve ser um número')
          .integer('Deve ser inteiro')
          .min(1)
          .max(10000),
      });

      await schema.validate(data, { abortEarly: false });

      if (selectedScopes.length === 0) {
        setScopesError('Selecione pelo menos um escopo.');
        return;
      }

      const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
      const allowed_ips = parseAllowedIps();
      const invalid = allowed_ips.filter(ip => !ipRegex.test(ip));
      if (invalid.length > 0) {
        toast.error(`IPs inválidos: ${invalid.join(', ')}`);
        return;
      }

      if (!allStores && selectedStores.length === 0) {
        toast.error(
          'Selecione pelo menos uma loja ou marque "Todas as lojas".'
        );
        return;
      }

      // 1. PATCH na aplicação (não inclui environment, scopes em separado)
      const updatePayload = {
        name: data.name?.trim(),
        description: data.description?.trim() || null,
        scopes: selectedScopes,
        rate_limit_per_minute: data.rate_limit_per_minute
          ? Number(data.rate_limit_per_minute)
          : 60,
        allowed_ips,
        manages_all_stores: allStores,
      };

      const { data: updated } = await api.patch(baseUrl, updatePayload);

      // 2. Sincroniza lojas se manages_all_stores=false
      if (!allStores) {
        const ids = selectedStores.map(s => s.value);
        await api.patch(`${baseUrl}/managed-stores/set-all`); // limpa primeiro
        // O backend já trocou manages_all_stores=true no set-all, agora setamos as lojas (que vai voltar pra false)
        await api.patch(`${baseUrl}/managed-stores/set`, { ids });
      } else {
        await api.patch(`${baseUrl}/managed-stores/set-all`);
      }

      toast.success('Aplicação atualizada com sucesso!');
      fillFormFromApplication(updated?.data || updated);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach(e => {
          validationErrors[e.path] = e.message;
        });
        formRef.current.setErrors(validationErrors);
        return;
      }
      if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error('Não foi possível salvar as alterações.');
      }
    }
  };

  // -----------------------------
  // Ações: rotate / revoke / reactivate
  // -----------------------------
  const handleRotateSecret = async () => {
    setRotateConfirmOpen(false);
    try {
      const { data } = await api.post(`${baseUrl}/rotate-secret`);
      const payload = data?.data;
      setRevealData({
        client_id: payload?.client_id,
        client_secret: payload?.client_secret,
      });
      setRevealOpen(true);
      toast.success('Novo secret gerado!');
      // Recarrega a application pra atualizar o preview
      fillFormFromApplication(payload);
    } catch (err) {
      if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error('Não foi possível rotacionar o secret.');
      }
    }
  };

  const handleReactivate = async () => {
    setReactivateConfirmOpen(false);
    try {
      const { data } = await api.post(`${baseUrl}/reactivate`);
      toast.success('Aplicação reativada!');
      fillFormFromApplication(data?.data || data);
    } catch (err) {
      if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error('Não foi possível reativar a aplicação.');
      }
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container className="container new-layout">
          <Loading />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container className="container new-layout">
        <PageHeader
          pageTitle="EDITAR APLICAÇÃO PARCEIRA"
          backTo="/aplicacoes-parceiras"
          responsivePosition="start"
          desktopPosition="center"
        />

        {/* Cabeçalho com info técnica + status */}
        <Card className="card new-layout mb-3">
          <CardBody className="card-body new-layout px-4">
            <Row className="w-100 mx-0 align-items-center">
              <Col lg={5} className="px-0">
                <Label className="text-muted font-size-12 mb-1">
                  Client ID
                </Label>
                <div className="d-flex align-items-center">
                  <code
                    style={{
                      fontFamily:
                        '"JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                      fontSize: '13px',
                      backgroundColor: '#f3f4f6',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {application?.client_id}
                  </code>
                  <IconButton
                    icon="las la-copy"
                    containerClass="ml-2"
                    fontSize={22}
                    color="info"
                    click={() =>
                      copyStringToClipboard(
                        application?.client_id,
                        'Client ID copiado!'
                      )
                    }
                    tip="Copiar"
                  />
                </div>
              </Col>
              <Col lg={4} className="px-0 pl-lg-3 mt-2 mt-lg-0">
                <Label className="text-muted font-size-12 mb-1">
                  Client Secret (preview)
                </Label>
                <div
                  style={{
                    fontFamily:
                      '"JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                    fontSize: '13px',
                    backgroundColor: '#f3f4f6',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    color: '#6b7280',
                  }}
                >
                  {application?.client_secret_preview}
                </div>
              </Col>
              <Col lg={3} className="px-0 pl-lg-3 mt-2 mt-lg-0" align="end">
                {status && (
                  <Badge
                    style={{
                      backgroundColor: status.bg,
                      color: '#fff',
                      fontSize: '12px',
                      padding: '6px 12px',
                      marginRight: 8,
                    }}
                  >
                    {status.label}
                  </Badge>
                )}
                <Badge
                  style={{
                    backgroundColor:
                      application?.environment === 'live'
                        ? '#2544b1'
                        : '#6b7280',
                    color: '#fff',
                    fontSize: '12px',
                    padding: '6px 12px',
                  }}
                >
                  {application?.environment === 'live' ? 'PRODUÇÃO' : 'TESTE'}
                </Badge>
              </Col>
            </Row>

            {application?.revoked_at && (
              <Alert color="danger" className="mt-3 mb-0">
                <strong>Aplicação revogada</strong> em{' '}
                {format(
                  parseISO(application.revoked_at),
                  "dd/MMM/yy 'às' HH'h'mm",
                  { locale: ptBR }
                )}
                {application.revoked_reason && (
                  <>
                    <br />
                    Motivo: {application.revoked_reason}
                  </>
                )}
              </Alert>
            )}

            {/* Ações rápidas */}
            <Row className="w-100 mx-0 mt-3">
              <Col
                className="px-0 d-flex flex-row flex-wrap"
                style={{ gap: 8 }}
              >
                <Button
                  color="info"
                  outline
                  className="d-inline-flex align-items-center"
                  onClick={() => setRotateConfirmOpen(true)}
                  disabled={Boolean(application?.revoked_at)}
                >
                  <i className="las la-sync-alt mr-1 font-size-16" />
                  Rotacionar Secret
                </Button>

                {application?.revoked_at ? (
                  <Button
                    color="success"
                    outline
                    className="d-inline-flex align-items-center"
                    onClick={() => setReactivateConfirmOpen(true)}
                  >
                    <i className="las la-undo mr-1 font-size-16" />
                    Reativar
                  </Button>
                ) : (
                  <Button
                    color="danger"
                    outline
                    className="d-inline-flex align-items-center"
                    onClick={() => setRevokeOpen(true)}
                  >
                    <i className="las la-ban mr-1 font-size-16" />
                    Revogar
                  </Button>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Form principal */}
        <Form ref={formRef} onSubmit={onSubmit}>
          <Card className="card new-layout">
            <CardBody className="card-body new-layout px-4 px-lg-5">
              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 px-lg-4">
                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <FormGroup>
                        <Label>Nome</Label>
                        <Input name="name" type="text" />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <FormGroup>
                        <Label>Descrição</Label>
                        <Input name="description" type="textarea" rows={3} />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <FormGroup>
                        <Label>Rate limit (requests por minuto)</Label>
                        <Input
                          name="rate_limit_per_minute"
                          type="number"
                          min={1}
                          max={10000}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <FormGroup>
                        <Label>
                          IPs permitidos (opcional)
                          <i
                            id="ips-tip-edit"
                            className="las la-info-circle font-size-18 ml-1"
                          />
                          <UncontrolledTooltip
                            placement="top"
                            target="ips-tip-edit"
                          >
                            Se preenchido, autenticação só é permitida desses
                            IPs. Em branco = qualquer IP.
                          </UncontrolledTooltip>
                        </Label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={allowedIpsText}
                          onChange={e => setAllowedIpsText(e.target.value)}
                          placeholder={'192.168.1.1\n10.0.0.5'}
                          style={{
                            fontFamily: '"JetBrains Mono", Menlo, monospace',
                            fontSize: '13px',
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>

                <Col lg={6} className="px-0 px-lg-4">
                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <Label>Escopos (permissões)</Label>
                      {scopesError && (
                        <p className="text-danger">{scopesError}</p>
                      )}
                      {AVAILABLE_SCOPES.map(scope => (
                        <CustomInput
                          type="checkbox"
                          key={scope.value}
                          id={`scope-edit-${scope.value}`}
                          name={`scope-edit-${scope.value}`}
                          label={scope.label}
                          checked={selectedScopes.includes(scope.value)}
                          className="text-primary mb-2"
                          onChange={() => toggleScope(scope.value)}
                        />
                      ))}
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0 mt-4">
                    <Col xs={6} className="px-0">
                      <Label>Lojas</Label>
                    </Col>
                    <Col xs={6} align="end" className="px-0">
                      <i
                        className={`las la-${
                          showStorePicker ? 'times' : 'plus'
                        } text-muted font-size-18 outline-none ${
                          allStores ? 'd-none' : ''
                        }`}
                        role="button"
                        tabIndex={0}
                        aria-label="add-store"
                        onClick={() => setShowStorePicker(!showStorePicker)}
                      />
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col className="px-0">
                      <FormGroup>
                        {!showStorePicker ? (
                          <CustomInput
                            type="checkbox"
                            id="allStoresEdit"
                            name="allStoresEdit"
                            label="Todas as lojas"
                            checked={allStores}
                            className="text-primary mt-2 mb-2"
                            onChange={() => {
                              setAllStores(!allStores);
                              if (!allStores === true) {
                                setSelectedStores([]);
                              }
                            }}
                          />
                        ) : (
                          <InputSelectAsync
                            loadOptions={loadStoreOptions}
                            name="store_picker_edit"
                            onChange={addStore}
                            placeholder="Buscar loja..."
                            type="text"
                            isClearable
                            loadingMessage={() => 'Buscando lojas...'}
                            onBlur={() => {}}
                          />
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {!allStores && (
                    <Row className="w-100 mx-0">
                      <Col className="px-0">
                        {selectedStores.length > 0 ? (
                          <PerfectScrollbar className="new-layout-managed-stores-scrollbar edit">
                            {selectedStores.map(store => (
                              <div
                                key={store.value}
                                className="store-ticket my-2"
                              >
                                <Row className="w-100">
                                  <Col xs={11} className="text-truncate pr-0">
                                    {store.label}
                                  </Col>
                                  <Col xs={1} className="px-0" align="end">
                                    <i
                                      className="las la-times text-muted font-size-16 outline-none"
                                      role="button"
                                      tabIndex={0}
                                      aria-label="remove-store"
                                      onClick={() => removeStore(store)}
                                    />
                                  </Col>
                                </Row>
                              </div>
                            ))}
                          </PerfectScrollbar>
                        ) : (
                          <Alert color="danger">
                            Selecione pelo menos uma loja ou marque &quot;Todas
                            as lojas&quot;.
                          </Alert>
                        )}
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Row className="w-100 mx-0 my-4">
            <Col
              align={defineAlignment(windowSize, LG, 'center', 'end')}
              className="px-0"
            >
              <Button
                color="primary"
                className="new-layout-btn main px-4 my-4"
                size="lg"
                type="submit"
              >
                Salvar Alterações
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>

      {/* Modal de confirmação: rotate */}
      <Modal
        isOpen={rotateConfirmOpen}
        toggle={() => setRotateConfirmOpen(false)}
      >
        <ModalHeader toggle={() => setRotateConfirmOpen(false)}>
          Rotacionar Client Secret
        </ModalHeader>
        <ModalBody>
          <Alert color="warning">
            O secret atual será invalidado imediatamente. Qualquer integração
            usando essas credenciais vai parar de autenticar até ser atualizada
            com o novo valor.
          </Alert>
          Tem certeza que deseja gerar um novo secret?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setRotateConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button color="info" onClick={handleRotateSecret}>
            Sim, rotacionar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal de confirmação: reactivate */}
      <Modal
        isOpen={reactivateConfirmOpen}
        toggle={() => setReactivateConfirmOpen(false)}
      >
        <ModalHeader toggle={() => setReactivateConfirmOpen(false)}>
          Reativar Aplicação
        </ModalHeader>
        <ModalBody>
          A aplicação será reativada e o parceiro poderá autenticar novamente
          usando o mesmo Client ID e Secret. Continuar?
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setReactivateConfirmOpen(false)}
          >
            Cancelar
          </Button>
          <Button color="success" onClick={handleReactivate}>
            Sim, reativar
          </Button>
        </ModalFooter>
      </Modal>

      <RevokeModal
        show={revokeOpen}
        setShow={setRevokeOpen}
        application={application}
        onRevoked={updated => {
          if (updated) fillFormFromApplication(updated);
        }}
      />

      <SecretRevealModal
        show={revealOpen}
        setShow={setRevealOpen}
        clientId={revealData?.client_id}
        clientSecret={revealData?.client_secret}
        isRotation
      />
    </div>
  );
};

export default EditPartnerApplication;
